import { Horizontal, Vertical, Texto, MaterialIcon, BBDTag, GraviButton } from '@gravitate-js/excalibrr'
import type { RouteItem } from '../../../types'
import { DriverStatusIcon } from '../../DriverStatusIcon'
import { ArrowDownOutlined, ArrowUpOutlined, ClockCircleOutlined, TruckFilled } from '@ant-design/icons'
import { IconCircleCheckFilled, IconCircleLetterM, IconMapPinCheck, IconRoad } from '@tabler/icons-react'
import { Popover, Tooltip } from 'antd'
import { getTimeDifference } from '../../../util'
import { dayjsWithTimezone, formatters } from '@/utils/intl/dateTimeFormatters'
import { StepTimePopover, TimePopoverContent } from './TimePopoverContent'
import '../styles.css'

type RouteStepItemProps = {
  step: RouteItem
  isLast?: boolean
  timezone: string
  onLocationClick: (coords: [number, number]) => void
}

export function RouteStepItem({ step, timezone, isLast, onLocationClick }: RouteStepItemProps) {
  const stepStatus = step.complete ? 'complete' : step.current ? 'in-progress' : 'upcoming'
  return (
    <Horizontal height={'100%'}>
      <Vertical flex={'none'} height={'auto'} className='route-step-timeline mr-4'>
        <span className={`dot ${stepStatus}`} />
        <div
          className={`route-step-connector ${step.complete ? 'complete' : 'incomplete'} ${isLast ? 'hidden' : ''}`}
        />
      </Vertical>
      <Vertical className='mb-4'>
        {step.action.includes('driving') ? (
          <>
            <TimeSpanBlock step={step} timezone={timezone} />

            <Horizontal verticalCenter gap={'5px'}>
              <GraviButton
                onClick={() => onLocationClick([step.origin_lon, step.origin_lat])}
                appearance='text'
                style={{ padding: '0 4px 0 4px', fontSize: '1.25em' }}
              >
                {step.origin_name}
              </GraviButton>
              <MaterialIcon icon='arrow_forward' size={18} className='route-step-arrow' />{' '}
              <GraviButton
                onClick={() => onLocationClick([step.destination_lon, step.destination_lat])}
                appearance='text'
                style={{ padding: '0 4px 0 4px', fontSize: '1.25em' }}
              >
                {step.destination_name}
              </GraviButton>
            </Horizontal>
          </>
        ) : (
          <>
            <TimeSpanBlock step={step} timezone={timezone} />

            <Horizontal>
              <GraviButton
                onClick={() => onLocationClick([step.destination_lon, step.destination_lat])}
                appearance='text'
                style={{ padding: '0 4px 0 4px', fontSize: '1.25em' }}
              >
                {step.destination_name}
              </GraviButton>
            </Horizontal>
            <Horizontal>
              <Texto appearance='hint'>
                {step.destination_address}, {step.destination_city}, {step.destination_state}
              </Texto>
            </Horizontal>
          </>
        )}
        <Horizontal alignItems='center' className='mt-1' gap={'10px'}>
          <MiniStepTag step={step} />
          <ProjectedStepTime step={step} />
          {step.route_distance && <RouteDistance step={step} />}
        </Horizontal>
      </Vertical>
    </Horizontal>
  )
}

export function ProjectedStepTime({ step }: { step: RouteItem }) {
  return (
    <Tooltip title={'Projected Time'}>
      <div className='flex' style={{ flexDirection: 'row', alignItems: 'center' }}>
        <ClockCircleOutlined className='mr-1' />
        <Texto>{getTimeDifference(step.projected_start_time, step.projected_end_time)}</Texto>
      </div>
    </Tooltip>
  )
}

export function RouteDistance({ step }: { step: RouteItem }) {
  return (
    <Tooltip title={'Route Distance'}>
      <Horizontal verticalCenter>
        <IconRoad size={16} className='mr-1' /> <Texto>{step.route_distance?.toFixed(1)} mi</Texto>
      </Horizontal>
    </Tooltip>
  )
}

export function TimeSpanBlock({ step, timezone }: { step: RouteItem; timezone: string }) {
  return (
    <Horizontal verticalCenter gap={8}>
      <DriverStatusIcon action={step?.action} />
      <Texto category='h4' className='flex-1' weight={'bold'}>
        {dayjsWithTimezone(step.start_time || step.projected_start_time, timezone).format('h:mm A')} —{' '}
        {formatters.TIME(step.end_time || step.projected_end_time, timezone)}
      </Texto>
      {step?.complete && (
        <Horizontal gap={4}>
          <StepTimePopover step={step} timezone={timezone} /> <ManualOrGPSTag step={step} />
        </Horizontal>
      )}
      {step.current && <BBDTag className='route-step-current-tag ml-2'>In Progress</BBDTag>}
    </Horizontal>
  )
}

export function MiniStepTag({ step }: { step: RouteItem }) {
  let icon = null
  let stepName = ''

  if (step.action.includes('driving')) {
    icon = <TruckFilled className='mr-1' />
    stepName = 'Driving'
  } else if (step.action === 'loading') {
    icon = <ArrowUpOutlined className='mr-1' />
    stepName = 'Loading'
  } else if (step.action === 'dropping') {
    icon = <ArrowDownOutlined className='mr-1' />
    stepName = 'Dropping'
  }
  return (
    <Horizontal>
      {icon}
      <Texto className={'ml-1'}>{stepName}</Texto>
    </Horizontal>
  )
}

export function ManualOrGPSTag({ step }: { step: RouteItem }) {
  const isManual = !(step.gps_start_event && step.gps_end_event)
  if (isManual) {
    return (
      <Tooltip title='Manual Time Entry'>
        <BBDTag warning style={{ padding: 2, margin: 0 }}>
          <Horizontal verticalCenter>
            <IconCircleLetterM size={16} color='var(--theme-warning)' />
          </Horizontal>
        </BBDTag>
      </Tooltip>
    )
  }
  return (
    <Tooltip title='GPS Tracked Time Entry'>
      <BBDTag theme2 style={{ padding: 2, margin: 0 }}>
        <Horizontal verticalCenter>
          <IconMapPinCheck size={16} />
        </Horizontal>
      </BBDTag>
    </Tooltip>
  )
}
